import React, { useEffect, useState } from "react";
import { getApi } from "../../../utils/api";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { API_GET_PROJECT_MEMBERS_BY_PROJECT_ID } from "../../../config/Endpoints";
import SelectableSortableTable from "./SelectableSortabletable";
import { displayFormatDate } from "../../../utils/helpers";
import { useProjectDesignationOptions } from "../../../Hooks/useProjectDesignationOptions";
import NoRecordsAvailable from "../../../components/NoRecordsAvailable";
import { ThreeDots } from "react-loader-spinner";
import "react-datepicker/dist/react-datepicker.css";
import CSATAddRatingModal from "./CSATAddRatingModal";

const CSATTeamMembersTable = ({
  projectId,
  membersData,
  shouldFetchMembers,
}) => {
  const { options: designationOptions } = useProjectDesignationOptions();
  const [members, setMembers] = useState(membersData || []);
  const [isLoading, setIsLoading] = useState(!membersData);
  const [selectedRows, setSelectedRows] = useState([]);
  const accessToken = Cookies.get("accessToken");
  const [showCSATModal, setShowCSATModal] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      // If we shouldn't fetch, just use membersData
      if (!shouldFetchMembers) {
        setMembers(membersData || []);
        setIsLoading(false);
        return;
      }

      // If no projectId, use membersData
      if (!projectId) {
        setMembers(membersData || []);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const membersRes = await getApi(
          `${API_GET_PROJECT_MEMBERS_BY_PROJECT_ID}/${projectId}/all-members`,
          accessToken,
        );

        let projectMembers = [];
        if (membersRes.data?.statusCode === 200) {
          projectMembers = Array.isArray(membersRes.data.data)
            ? membersRes.data.data
            : membersRes.data.data.members || [];
        }

        // Merge with shadow resources (membersData)
        const mergedMembers = [...projectMembers, ...(membersData || [])];
        setMembers(mergedMembers);
      } catch (err) {
        if (err.response?.status !== 404) {
          toast.error(err.message || "Error fetching team members");
        }
        setMembers(membersData || []);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [projectId, membersData, accessToken, shouldFetchMembers]);

  const columns = [
    {
      key: "employeeName",
      label: "Employee Name",
      render: (row) => {
        const name =
          row.shadowEmployeeName ||
          row.employeeName ||
          `${row.fullName}`.trim();

        return name ? name.replace(/([a-z])([A-Z])/g, "$1 $2") : "—";
      },
    },
    {
      key: "designation",
      label: "Designation",
      render: (row) => {
        if (row.isProjectRole) {
          switch (row.designation) {
            case "project_manager":
              return "Project Manager";
            case "delivery_lead":
              return "Delivery Lead";
            case "principal_sponsor":
              return "Principal Sponsor";
            case "lead_business_analyst":
              return "Lead Business Analyst";
            default:
              return row.designation;
          }
        }

        const found = designationOptions.find(
          (d) => d.value === row.designation,
        );
        return found ? found.label : row.designation;
      },
    },
    {
      key: "joiningDate",
      label: "Joining Date",
      render: (row) =>
        row.joiningDate
          ? displayFormatDate(row.joiningDate)
          : row.createdDate
            ? displayFormatDate(row.createdDate)
            : "—",
    },

    {
      key: "status",
      label: "Status",
      render: (row) => {
        switch (row.status) {
          case 1:
            return "Active";
          case 2:
            return "Deallocated";
          default:
            return "Inactive";
        }
      },
    },
  ];

  return (
    <div className="mt-3">
      <h6 className="mb-2">Team Members</h6>
      {isLoading ? (
        <div className="centered-loader">
          <ThreeDots height="80" width="80" color="blue" ariaLabel="loading" />
        </div>
      ) : members.length === 0 ? (
        <NoRecordsAvailable />
      ) : (
        <SelectableSortableTable
          columns={columns}
          data={members}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          getRowId={(row) => row._tempId}
          tableBodyClass="selectable-table-body--hover"
        />
      )}
      {selectedRows.length > 0 && (
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            className="theme-button bg-grey"
            onClick={() => setSelectedRows([])}
          >
            Cancel
          </button>
          <button
            className="theme-button"
            onClick={() => setShowCSATModal(true)}
          >
            Next
          </button>

          <CSATAddRatingModal
            show={showCSATModal}
            selectedEmployees={members
              .map((m, index) => ({ ...m, _tempId: index + 1 }))
              .filter((m) => selectedRows.includes(m._tempId))}
            projectId={projectId}
            onFinish={(data) => {
              console.log("CSAT Submitted:", data);
              setShowCSATModal(false);
              setSelectedRows([]);
            }}
            onClose={() => setShowCSATModal(false)}
          />
        </div>
      )}
    </div>
  );
};

export default CSATTeamMembersTable;
